import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { from, of, race, Subject } from 'rxjs';
import { RecaptchaComponent } from 'ng-recaptcha';
import { CompanyService } from '@app/shared/services/company.service';
import { environment } from '@environments/environment';
import { featureFlags } from '@utils/feature-flags';

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private companyService: CompanyService,
    private ngZone: NgZone
  ) {}

  get isEnabled() {
    const isCompany = Boolean(this.companyService.subdomain);
    const isEnabledForCompany = featureFlags.isOn('CompanyFeature.reCaptcha');

    return !isCompany || isEnabledForCompany;
  }

  execute(recaptcha?: RecaptchaComponent) {
    if (recaptcha) {
      this.ngZone.runOutsideAngular(() => {
        recaptcha.execute();
      });
    }

    return this.resolvedOrDismissed(recaptcha);
  }

  resolvedOrDismissed(recaptcha?: RecaptchaComponent) {
    if (recaptcha) {
      return race(from(recaptcha.resolved), this.rejectWhenDismissed());
    } else {
      return of('');
    }
  }

  /**
   * Used for invisible recaptcha.
   *
   * Returns observable which emits when recaptcha window is closed.
   */
  rejectWhenDismissed(pollInterval = 100) {
    if (!this.isEnabled) {
      return of('');
    }

    const subj = new Subject<string>();

    this.ngZone.runOutsideAngular(() => {
      const container = this.document.querySelector('iframe[src*="google.com/recaptcha/api2/bframe"]').parentNode
        .parentNode as HTMLElement;
      let wasVisible = false;

      const whenComplete = setInterval(() => {
        if (container.style.visibility === 'visible') {
          wasVisible = true;
        } else if (wasVisible) {
          clearInterval(whenComplete);

          this.ngZone.run(() => {
            subj.next(null);
            subj.complete();
          });
        }
      }, pollInterval);
    });

    return subj.asObservable();
  }

  get libraryUrlV3() {
    return `https://www.google.com/recaptcha/api.js?render=${environment.RECAPTCHA_V3_SITE_KEY}`;
  }

  loadLibraryV3() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.onload = () => {
        const recaptcha = window.grecaptcha;

        recaptcha.ready(() => {
          resolve(recaptcha);
        });
      };
      script.onerror = () => reject();
      script.src = this.libraryUrlV3;

      document.head.appendChild(script);
    });
  }
}
